import React, { useEffect, useRef, useState } from "react";

import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import SideNav from "component/layouts/sideNav";
import {
  setItems,
  setProjectManagers,
  setSites,
  setSupplierCategories,
  setSuppliers,
  setUoms,
  setWorkOrders,
  siteCellRenderer,
  supplierCellRenderer,
  setUsers,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  setAlertConfirmDeletion,
  setAlertRadioConfirmation,
  setAlertTitleText,
} from "component/modal/alertModal";

import {
  ADD,
  DELETE,
  GO,
  LABOUR_MANAGEMENT,
  SUB_CONTRACTOR_BILLING,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_LOGIN,
  PATH_SUB_CONTRACTOR_BILLING,
  PATH_SUB_CONTRACTOR_BILLING_DETAIL,
} from "resources/constant/url/url";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APILABOURMGMT } from "apihandler/labourmgmt";

import { setDates, setFilterModel } from "store/Slice/subconBillingSlice";


export default function SubContractorBilling() {
  const { date } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef();

  const dispatch = useDispatch();
  const { fromDate, toDate, filterModel } = useSelector((s) => s.subconBilling);

  const [dateValue, setDateValue] = useState(null);
  const [toDateValue, setToDateValue] = useState(dayjs().endOf("month"));

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, []);

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;
    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      console.log("delete the selected ids: ", selectedRowIds);
      APILABOURMGMT.DeleteSubContractorBillings(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };



  const onRowSelectionChanged = (event) => {
    const ids = event.api.getSelectedRows().map((r) => r.id);
    setSelectedRowIds(ids);
  };

  const columnDefs = [
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Entry Date",
      field: "entryDate",
      checkboxSelection: false,
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      flex: 1.5,
      headerName: "Reference No",
      field: "referenceNumber",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1.5,
      headerName: "Contractor Name",
      field: "contractorId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (p) => supplierCellRenderer({ value: p.data.contractorId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      flex: 1.5,
      headerName: "Site",
      field: "siteId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (p) => siteCellRenderer({ value: p.data.siteId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Bill Amount",
      field: "amountTotalBill",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Amount Paid",
      field: "totalAmountPaid",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      valueGetter: (p) => userCellRenderer({ value: p.data.modifiedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      autoHeight: true,
      wrapText: true,
    },
    {
      flex: 1,
      headerName: "Details",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            aria-label="edit"
            onClick={() => {
              navigate(PATH_SUB_CONTRACTOR_BILLING_DETAIL + params.data.id, {
                state: {
                  date: dayjs(dateValue).format("YYYY-MM-DD"),
                  isEdit: true,
                },
              });
            }}
          >
            <EditIcon style={{ color: PRUSSIAN_BLUE, width: 20 }} />
          </IconButton>
          <IconButton
            aria-label="view"
            onClick={() => {
              navigate(PATH_SUB_CONTRACTOR_BILLING_DETAIL + params.data.id, {
                state: {
                  date: dayjs(dateValue).format("YYYY-MM-DD"),
                  isEdit: false,
                },
              });
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
          <IconButton
            aria-label="edit"
            onClick={() => {
              let exportExcel = "";
              setAlertRadioConfirmation(
                { excel: "Excel", pdf: "PDF" },
                "Please select an option..."
              ).then((result) => {
                if (!result.isConfirmed) return;

                console.log(result);
                let filename = "";
                exportExcel = result.value === "excel";
                APILABOURMGMT.SubContractorBillingExport(
                  params.data.id,
                  result.value === "excel"
                )
                  .then((res) => {
                    const contentType = res.headers.get("content-type");
                    if (contentType.startsWith("application/json")) {
                      return Promise.reject(res.json());
                    } else {
                      const header = res.headers.get("Content-Disposition");
                      if (header !== null) {
                        const parts = header.split(";");
                        filename = parts[1].split("=")[1].replaceAll('"', "");
                      } else {
                        filename = `subcontractorbilling_${params.data.id}.html`;
                      }

                      return res.blob();
                    }
                  })
                  .then((blob) => {
                    if (blob !== null) {
                      if (exportExcel) {
                        let url = window.URL.createObjectURL(blob);
                        let a = document.createElement("a");
                        a.href = url;
                        a.download = filename.replace(".html", ".xlsx");
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      } else {
                        blob.text().then((x) => {
                          let html = x;
                          html = html.replace(
                            "<script>",
                            '<script type="text/javascript">'
                          );
                          let blob = new Blob([html], { type: "text/html" });
                          let url = window.URL.createObjectURL(blob, {
                            oneTimeOnly: true,
                          });

                          let win = window.open(url, "_blank", "");
                          setTimeout(() => {
                            win.print();
                          }, 2000);
                        });
                      }
                    }
                  })
                  .catch((m) => {
                    setAlertTitleText(
                      "Oops...",
                      `Something went wrong...${m.message}`
                    );
                  });
              });
            }}
          >
            <PrintIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
  };

  const fetchSubContractorBilling = (d1, d2) => {
    return APILABOURMGMT.SubcontractorBillings(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        return resp.response.sort((a, b) => b.id - a.id);
      });
  };

  const onGridReady = async (params) => {
    const d1 = date ? dayjs(date) : dayjs(fromDate || dayjs().startOf("month"));
    const d2 = date
      ? dayjs(toDateValue)
      : dayjs(toDate || dayjs().endOf("month"));
    setDateValue(d1);
    setToDateValue(d2);

    APIMASTER.UomOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUoms(r.response));
    APIMASTER.SiteOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSites(r.response));
    APIMASTER.SupplierCategories()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSupplierCategories(r.response));
    APILABOURMGMT.WorkOrderOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setWorkOrders(r.response));
    APIMASTER.ItemOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setItems(r.response));
    APIMASTER.SupplierOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSuppliers(r.response));
    APIMASTER.UserOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUsers(r.response));
    APIMASTER.ProjectManagerOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setProjectManagers(r.response));

    const data = await fetchSubContractorBilling(d1, d2);
    params.api.setRowData(data);

    if (filterModel) params.api.setFilterModel(filterModel);
  };

  const onFilterChanged = (event) => {
    const model = event.api.getFilterModel();
    dispatch(setFilterModel(model));
  };

  return (
    <SideNav
      currentComponent={LABOUR_MANAGEMENT}
      subComponent={SUB_CONTRACTOR_BILLING}
      subComponentPath={PATH_SUB_CONTRACTOR_BILLING}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_SUB_CONTRACTOR_BILLING_DETAIL + 0, {
              state: {
                date: dayjs(dateValue).format("YYYY-MM-DD"),
                isEdit: false,
              },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(v) => setDateValue(v)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(v) => setToDateValue(v)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
            }}
            onClick={async () => {
              dispatch(
                setDates({
                  fromDate: dayjs(dateValue).format("YYYY-MM-DD"),
                  toDate: dayjs(toDateValue).format("YYYY-MM-DD"),
                })
              );

              const data = await fetchSubContractorBilling(
                dayjs(dateValue),
                dayjs(toDateValue)
              );
              gridRef.current.api.setRowData(data);

              if (filterModel) gridRef.current.api.setFilterModel(filterModel);
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(p) => p.data.id}
            onSelectionChanged={onRowSelectionChanged}
            onFilterChanged={onFilterChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
